import { useState, useCallback } from "react";
import { generateBoard, generateDailyBoard, dailyKey } from "./nameEngine";

const MAX_GUESSES = 4;

// Build a spoiler-free share grid from the sequence of guess results.
// Each Lock In contributes a row of 5 squares: green = impostor correctly in
// your five, gray = a slot that wasn't an impostor. Order within a row is
// sorted (greens first) so it never reveals *which* tiles — only how many.
function buildShareGrid(history, won) {
  const rows = history.map((correct) => {
    const green = "\uD83D\uDFE9".repeat(correct);   // 🟩
    const gray = "\u2B1C".repeat(5 - correct);        // ⬜
    return green + gray;
  });
  const header = `Buttfumbles Starting Five — ${dailyKey()}`;
  const score = won ? `${history.length}/${MAX_GUESSES}` : `X/${MAX_GUESSES}`;
  return `${header}\n${score}\n${rows.join("\n")}\nbuttfumbles.com`;
}

const sfStyles = `
@font-face { font-family: 'Barlow'; font-style: normal; font-weight: 400; font-display: swap; src: url('/fonts/Barlow-Regular.woff2') format('woff2'); }
@font-face { font-family: 'Barlow'; font-style: normal; font-weight: 500; font-display: swap; src: url('/fonts/Barlow-Medium.woff2') format('woff2'); }
@font-face { font-family: 'Barlow'; font-style: normal; font-weight: 600; font-display: swap; src: url('/fonts/Barlow-SemiBold.woff2') format('woff2'); }
@font-face { font-family: 'Barlow Condensed'; font-style: normal; font-weight: 400; font-display: swap; src: url('/fonts/BarlowCondensed-Regular.woff2') format('woff2'); }
@font-face { font-family: 'Barlow Condensed'; font-style: normal; font-weight: 600; font-display: swap; src: url('/fonts/BarlowCondensed-SemiBold.woff2') format('woff2'); }
@font-face { font-family: 'Barlow Condensed'; font-style: normal; font-weight: 700; font-display: swap; src: url('/fonts/BarlowCondensed-Bold.woff2') format('woff2'); }
.sf * { margin:0; padding:0; box-sizing:border-box; }
.sf { min-height:100vh; background:#f5f4f2; font-family:'Barlow',sans-serif; color:#1a1a2e; display:flex; flex-direction:column; align-items:center; padding:20px 16px 48px; }
.sf-top { width:100%; max-width:560px; display:flex; align-items:center; justify-content:space-between; margin-bottom:6px; }
.sf-back { background:none; border:none; font-family:'Barlow Condensed',sans-serif; font-size:14px; font-weight:700; letter-spacing:2px; text-transform:uppercase; color:#999; cursor:pointer; padding:6px 4px; }
.sf-back:hover { color:#e7589b; }
.sf-streak { font-family:'Barlow Condensed',sans-serif; font-size:13px; font-weight:700; letter-spacing:1px; color:#77b2ee; text-transform:uppercase; }
.sf-title { font-family:'Barlow Condensed',sans-serif; font-weight:700; font-size:clamp(30px,8vw,44px); letter-spacing:3px; text-transform:uppercase; color:#e7589b; line-height:1; margin:4px 0 2px; text-align:center; }
.sf-sub { font-family:'Barlow Condensed',sans-serif; font-size:13px; font-weight:600; letter-spacing:3px; text-transform:uppercase; color:#999; text-align:center; margin-bottom:12px; }
.sf-modes { display:flex; gap:0; margin-bottom:16px; border:1.5px solid #e0ddda; border-radius:8px; overflow:hidden; }
.sf-mode { padding:7px 20px; font-family:'Barlow Condensed',sans-serif; font-size:13px; font-weight:700; letter-spacing:2px; text-transform:uppercase; background:#fff; color:#999; border:none; cursor:pointer; transition:all .15s; }
.sf-mode.on { background:#e7589b; color:#fff; }
.sf-mode:not(.on):hover { background:#faf7f5; color:#666; }
.sf-status { width:100%; max-width:560px; display:flex; align-items:center; justify-content:space-between; margin-bottom:12px; padding:0 2px; }
.sf-count { font-family:'Barlow Condensed',sans-serif; font-size:16px; font-weight:700; letter-spacing:1px; color:#1a1a2e; }
.sf-count b { color:#e7589b; }
.sf-guesses { display:flex; gap:6px; align-items:center; }
.sf-pip { width:12px; height:12px; border-radius:50%; background:#e7589b; box-shadow:0 1px 3px rgba(231,88,155,0.4); transition:all .2s; }
.sf-pip.spent { background:#e0ddda; box-shadow:none; }
.sf-feedback { width:100%; max-width:560px; min-height:26px; text-align:center; font-family:'Barlow Condensed',sans-serif; font-size:16px; font-weight:700; letter-spacing:1px; margin-bottom:12px; }
.sf-feedback.info { color:#77b2ee; }
.sf-feedback.win { color:#2fa36b; }
.sf-feedback.lose { color:#e7589b; }
.sf-grid { width:100%; max-width:560px; display:grid; grid-template-columns:repeat(3,1fr); gap:9px; }
@media (min-width:480px){ .sf-grid { grid-template-columns:repeat(5,1fr); } }
.sf-tile { position:relative; background:#fff; border:2px solid #e0ddda; border-radius:9px; padding:12px 6px; min-height:78px; display:flex; align-items:center; justify-content:center; text-align:center; cursor:pointer; transition:transform .1s, border-color .15s, background .15s, box-shadow .15s; user-select:none; }
.sf-tile:hover { border-color:#c8c4c0; }
.sf-tile:active { transform:scale(0.97); }
.sf-tile.sel { border-color:#e7589b; background:#fdeef5; box-shadow:0 3px 12px rgba(231,88,155,0.18); }
.sf-tile.locked { cursor:default; }
.sf-tile.locked:hover { border-color:#e0ddda; }
.sf-tile.reveal-fake { border-color:#2fa36b; background:#eafaf1; }
.sf-tile.reveal-real { opacity:.55; }
.sf-tile.reveal-miss { border-color:#e7589b; background:#fdeef5; }
.sf-name { font-family:'Barlow Condensed',sans-serif; font-weight:600; font-size:clamp(13px,3.4vw,16px); line-height:1.1; letter-spacing:.3px; color:#1a1a2e; }
.sf-tag { position:absolute; top:5px; right:6px; font-family:'Barlow Condensed',sans-serif; font-size:9px; font-weight:700; letter-spacing:1px; text-transform:uppercase; }
.sf-tag.fake { color:#2fa36b; }
.sf-tag.miss { color:#e7589b; }
.sf-src { position:absolute; bottom:4px; left:0; right:0; font-family:'Barlow',sans-serif; font-size:9px; color:#8a8a8a; letter-spacing:.2px; }
.sf-actions { width:100%; max-width:560px; display:flex; gap:10px; margin-top:18px; justify-content:center; flex-wrap:wrap; }
.sf-btn { padding:12px 34px; font-family:'Barlow Condensed',sans-serif; font-size:16px; font-weight:700; letter-spacing:2px; text-transform:uppercase; border:none; border-radius:8px; cursor:pointer; transition:transform .15s, box-shadow .15s, background .15s; }
.sf-btn.primary { background:#e7589b; color:#fff; }
.sf-btn.primary:hover { background:#d44d8b; transform:translateY(-2px); box-shadow:0 4px 16px rgba(231,88,155,0.35); }
.sf-btn.primary:disabled { opacity:.3; cursor:default; transform:none; box-shadow:none; }
.sf-btn.ghost { background:#fff; color:#666; border:1.5px solid #ddd; }
.sf-btn.ghost:hover { background:#f5f5f5; color:#333; }
.sf-reveal-note { width:100%; max-width:560px; margin-top:16px; font-family:'Barlow',sans-serif; font-size:13px; color:#888; text-align:center; line-height:1.5; }
.sf-reveal-note b { color:#2fa36b; font-family:'Barlow Condensed',sans-serif; letter-spacing:.5px; }
`;

export default function StartingFive({ onExit }) {
  const [mode, setMode] = useState("daily"); // daily | free
  const [board, setBoard] = useState(() => generateDailyBoard());
  const [selected, setSelected] = useState(new Set());
  const [guessesLeft, setGuessesLeft] = useState(MAX_GUESSES);
  const [phase, setPhase] = useState("playing"); // playing | won | lost
  const [feedback, setFeedback] = useState(null); // {text, kind}
  const [streak, setStreak] = useState(0);
  const [guessHistory, setGuessHistory] = useState([]); // per-lock-in correct counts
  const [copied, setCopied] = useState(false);

  const newBoard = useCallback((keepStreak) => {
    setBoard(mode === "daily" ? generateDailyBoard() : generateBoard());
    setSelected(new Set());
    setGuessesLeft(MAX_GUESSES);
    setPhase("playing");
    setFeedback(null);
    setGuessHistory([]);
    setCopied(false);
    if (!keepStreak) setStreak(0);
  }, [mode]);

  const switchMode = useCallback((next) => {
    if (next === mode) return;
    setMode(next);
    setBoard(next === "daily" ? generateDailyBoard() : generateBoard());
    setSelected(new Set());
    setGuessesLeft(MAX_GUESSES);
    setPhase("playing");
    setFeedback(null);
    setGuessHistory([]);
    setCopied(false);
    setStreak(0);
  }, [mode]);

  const shareResult = useCallback(async () => {
    const text = buildShareGrid(guessHistory, phase === "won");
    try {
      if (navigator.share) await navigator.share({ text });
      else { await navigator.clipboard.writeText(text); setCopied(true); }
    } catch { /* user dismissed share sheet */ }
  }, [guessHistory, phase]);

  const toggle = (id) => {
    if (phase !== "playing") return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else if (next.size < 5) next.add(id);
      else return prev; // cap at 5
      return next;
    });
    setFeedback(null);
  };

  const lockIn = () => {
    if (selected.size !== 5 || phase !== "playing") return;
    const correct = [...selected].filter((id) => board[id].isFake).length;
    setGuessHistory((h) => [...h, correct]);
    if (correct === 5) {
      setPhase("won");
      setStreak((s) => s + 1);
      setFeedback({ text: "Starting Five confirmed — you called all five impostors.", kind: "win" });
      return;
    }
    const left = guessesLeft - 1;
    setGuessesLeft(left);
    if (left <= 0) {
      setPhase("lost");
      setStreak(0);
      setFeedback({ text: "Out of guesses — impostors slipped into your five.", kind: "lose" });
    } else {
      setFeedback({ text: `${correct} of your 5 are impostors. Swap and try again.`, kind: "info" });
    }
  };

  const revealed = phase !== "playing";
  const tileClass = (t) => {
    if (!revealed) return selected.has(t.id) ? "sf-tile sel" : "sf-tile";
    if (t.isFake) return "sf-tile locked reveal-fake";
    if (selected.has(t.id)) return "sf-tile locked reveal-miss";
    return "sf-tile locked reveal-real";
  };

  return (
    <>
      <style>{sfStyles}</style>
      <div className="sf">
        <div className="sf-top">
          <button className="sf-back" onClick={onExit}>← Generator</button>
          <span className="sf-streak">Streak: {streak}</span>
        </div>
        <div className="sf-title">Starting Five</div>
        <div className="sf-sub">{mode === "daily" ? `Daily · ${dailyKey()}` : "Start the 5 impostors"}</div>

        <div className="sf-modes">
          <button className={`sf-mode ${mode === "daily" ? "on" : ""}`} onClick={() => switchMode("daily")}>Daily</button>
          <button className={`sf-mode ${mode === "free" ? "on" : ""}`} onClick={() => switchMode("free")}>Free Play</button>
        </div>

        <div className="sf-status">
          <span className="sf-count">Selected: <b>{selected.size}</b>/5</span>
          <span className="sf-guesses">
            {Array.from({ length: MAX_GUESSES }).map((_, i) => (
              <span key={i} className={`sf-pip ${i >= guessesLeft ? "spent" : ""}`} />
            ))}
          </span>
        </div>

        <div className={`sf-feedback ${feedback ? feedback.kind : ""}`}>
          {feedback ? feedback.text : "Five of these fifteen are impostors. Start all five."}
        </div>

        <div className="sf-grid">
          {board.map((t) => (
            <div key={t.id} className={tileClass(t)} onClick={() => toggle(t.id)}>
              {revealed && t.isFake && <span className="sf-tag fake">Fake</span>}
              {revealed && !t.isFake && selected.has(t.id) && <span className="sf-tag miss">Real</span>}
              <span className="sf-name">{t.name}</span>
              {revealed && t.isFake && <span className="sf-src">was: {t.sourceLast}</span>}
            </div>
          ))}
        </div>

        <div className="sf-actions">
          {!revealed && (
            <button className="sf-btn primary" onClick={lockIn} disabled={selected.size !== 5}>
              Lock In
            </button>
          )}
          {revealed && mode === "daily" && (
            <button className="sf-btn primary" onClick={shareResult}>
              {copied ? "Copied!" : "Share Result"}
            </button>
          )}
          {revealed && mode === "free" && (
            <button className="sf-btn primary" onClick={() => newBoard(phase === "won")}>
              {phase === "won" ? "Next Board" : "New Board"}
            </button>
          )}
          <button
            className="sf-btn ghost"
            onClick={revealed ? (mode === "daily" ? () => switchMode("free") : () => newBoard(false)) : onExit}
          >
            {revealed ? (mode === "daily" ? "Play Free" : "Reset Streak") : "Back"}
          </button>
        </div>

        {revealed && (
          <div className="sf-reveal-note">
            The fakes were spun off real athletes:{" "}
            {board.filter((t) => t.isFake).map((t, i, a) => (
              <span key={t.id}>
                <b>{t.name}</b> ← {t.source}{i < a.length - 1 ? " · " : ""}
              </span>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
